// Where the camera may go (SPEC-viewfinder §1.3, owner's ruling 6).
//
// The centre is clamped by us, not by MapLibre's `maxBounds`. `maxBounds`
// constrains the VIEWPORT, and ours is up to 10 km across at r = 5 km and is
// `--disc`-dependent per breakpoint, so a viewport bound would stop the centre
// at a different place on every device. Clamping the centre is one number and
// it is literally what „hard stop at the edge" means.
//
// Pure on purpose: `state/` may import only `shared/` (ADR-0013), this reads no
// DOM and no clock, and the acceptance row `a drag stops at the city's edge`
// is proved here as well as in the browser.

/**
 * The box the centre may stand in, in degrees.
 *
 * Derived, not chosen: `src/state/districts.json` carries 71 district
 * centroids whose bounding box measures lat 52.30793–52.49833, lon
 * 16.76591–17.04683. Padded by 3 km (0,027° lat, 0,044° lon at 52,4° N) to
 * reach the administrative edge beyond the outermost centroid — about
 * 25 × 27 km.
 */
export const CITY_BOUNDS = {
  south: 52.28,
  west: 16.72,
  north: 52.53,
  east: 17.09,
} as const;

/** A point in the world, in the order the rest of this app writes it. */
export type Point = { lat: number; lon: number };

const between = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));

/**
 * The nearest point inside {@link CITY_BOUNDS}. A point already inside is
 * returned with its own numbers, so a caller may compare identity of value
 * rather than of object and still see „nothing moved".
 *
 * There is no bounce and no message (§1.3): the drag stops, the glide stops,
 * and the rings go on measuring whatever is under them.
 */
export function clampCentre(point: Point): Point {
  return {
    lat: between(point.lat, CITY_BOUNDS.south, CITY_BOUNDS.north),
    lon: between(point.lon, CITY_BOUNDS.west, CITY_BOUNDS.east),
  };
}

/** Whether the point is already inside the box — the cheap test a per-frame path wants. */
export function insideBounds(point: Point): boolean {
  return (
    point.lat >= CITY_BOUNDS.south &&
    point.lat <= CITY_BOUNDS.north &&
    point.lon >= CITY_BOUNDS.west &&
    point.lon <= CITY_BOUNDS.east
  );
}
